import { api, apiClient } from "../lib";
import type {
  LegacyCaseDetail,
  LegacyCaseState,
  LegacyCaseSummary,
  LegacyImportRequest,
  LegacySaveReviewRequest,
  LegacyImportedCaseProjection,
  LegacyCaseCollision,
  LegacyExistingCaseAttachmentRequest,
  LegacyExistingCaseAttachmentResult,
} from "../types/legacyCases";

export interface LegacyCaseFilters {
  page: number;
  limit: number;
  search?: string;
  status?: LegacyCaseState;
}

export interface LegacyCaseList {
  data: LegacyCaseSummary[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface LegacyImportResult {
  caseId: string;
  archiveId: string;
  alreadyImported: boolean;
}

const CONFLICT_MESSAGES = {
  source_conflicts_unacknowledged:
    "The spreadsheet and report need comparison. Review the highlighted source values, correct the draft, and acknowledge the differences before importing.",
  extraction_review_required:
    "A report extraction is available. Select it, compare it with your draft, and save the reviewed draft before importing.",
  stale_review:
    "A newer review was saved. Your edits are retained; compare both drafts before saving again.",
  patient_match_required:
    "A matching patient already exists. Select the existing patient before importing; your reviewed information is retained.",
  patient_selection_changed:
    "The selected patient changed or is no longer available. Refresh the patient choices, compare the current identity, and choose Use this patient again. Your draft is retained.",
  patient_candidates_review_required:
    "Existing patient suggestions need review. Refresh the suggestions, compare their identities, and confirm that a new patient is needed. Your draft is retained.",
  case_number_exists:
    "An active case already uses this case number. Review the existing case before proceeding; your draft is retained.",
  stale_case_collision:
    "The existing case for this number changed. Refresh the case check and review the destination before attaching a document. Your draft is retained.",
  legacy_attachment_content_conflict:
    "An existing document could not be verified against this archive. No attachment was changed. Review the existing case files before trying again.",
  legacy_attachment_unavailable:
    "The archived document could not be copied and verified. No attachment was added. Your draft is retained; try again when document storage is available.",
  already_imported:
    "This archive has already been imported. Open the imported case to continue.",
  idempotency_key_conflict:
    "This import request conflicts with another request. Your draft is retained; try confirming the import again.",
  concurrent_change:
    "Another change interrupted this request. Your draft is retained; compare the saved review before retrying.",
} as const;

export type LegacyCaseConflictCode = keyof typeof CONFLICT_MESSAGES;

export class LegacyCaseRequestError extends Error {
  readonly status?: number;

  readonly code?: LegacyCaseConflictCode;

  constructor(status?: number, code?: LegacyCaseConflictCode) {
    const message =
      ((status === 409 && code !== "legacy_attachment_unavailable") ||
        (status === 503 && code === "legacy_attachment_unavailable")) &&
      code
        ? CONFLICT_MESSAGES[code]
        : status === 409
        ? "A conflicting record prevents this request. Your edits are retained. Review the selected patient, case number, and saved draft."
        : status === 403
        ? "Your account cannot access this legacy case. Contact an administrator."
        : status === 404
        ? "This legacy case or document is no longer available. Return to Legacy Cases and open it again."
        : status === 400 || status === 422
        ? "The review could not be accepted. Check the required fields, selected records, and source evidence."
        : "The request could not be completed. Your edits are still here. Check your connection and try again.";
    super(message);
    this.name = "LegacyCaseRequestError";
    this.status = status;
    this.code =
      (status === 409 && code !== "legacy_attachment_unavailable") ||
      (status === 503 && code === "legacy_attachment_unavailable")
        ? code
        : undefined;
  }
}

function translateLegacyError(error: unknown): LegacyCaseRequestError {
  if (error instanceof LegacyCaseRequestError) return error;
  const response = (
    error as {
      response?: { status?: unknown; data?: { code?: unknown } };
    } | null
  )?.response;
  const status =
    typeof response?.status === "number" ? response.status : undefined;
  const candidate =
    response?.data?.code || (error as { code?: unknown } | null)?.code;
  const code =
    typeof candidate === "string" &&
    Object.prototype.hasOwnProperty.call(CONFLICT_MESSAGES, candidate)
      ? (candidate as LegacyCaseConflictCode)
      : undefined;
  return new LegacyCaseRequestError(status, code);
}

async function request<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    throw translateLegacyError(error);
  }
}

export const legacyCasesService = {
  list: (filters: LegacyCaseFilters) =>
    request(() =>
      api.get<LegacyCaseList>("/legacy-cases", { params: filters })
    ),
  get: (id: string) =>
    request(() => api.get<LegacyCaseDetail>(`/legacy-cases/${id}`)),
  collision: (id: string) =>
    request(() =>
      api.get<LegacyCaseCollision>(`/legacy-cases/${id}/collision`)
    ),
  attachToExistingCase: (
    id: string,
    payload: LegacyExistingCaseAttachmentRequest
  ) =>
    request(() =>
      api.post<LegacyExistingCaseAttachmentResult>(
        `/legacy-cases/${id}/existing-case-documents`,
        payload
      )
    ),
  prepare: (id: string) =>
    request(() =>
      api.post<LegacyCaseDetail>(
        `/legacy-cases/${id}/prepare`,
        {},
        { timeout: 120000 }
      )
    ),
  imported: (caseId: string) =>
    request(() =>
      api.get<LegacyImportedCaseProjection>(`/cases/${caseId}/legacy-import`)
    ),
  physicians: (id: string, search: string) =>
    request(() =>
      api.get<LegacyCaseDetail["physicianCandidates"]>(
        `/legacy-cases/${id}/physicians`,
        { params: { search } }
      )
    ),
  patients: (id: string, search: string) =>
    request(() =>
      api.get<LegacyCaseDetail["patientCandidates"]>(
        `/legacy-cases/${id}/patients`,
        { params: { search } }
      )
    ),
  patientCandidates: (id: string, review: LegacySaveReviewRequest) =>
    request(() =>
      api.post<LegacyCaseDetail["patientCandidates"]>(
        `/legacy-cases/${id}/patient-candidates`,
        review
      )
    ),
  attachRequisition: (
    id: string,
    payload: { s3Key: string; originalFileName: string }
  ) =>
    request(() =>
      api.post<LegacyCaseDetail>(`/legacy-cases/${id}/documents`, {
        ...payload,
        kind: "requisition",
      })
    ),
  save: (id: string, review: LegacySaveReviewRequest) =>
    request(() =>
      api.put<LegacyCaseDetail>(`/legacy-cases/${id}/review`, review)
    ),
  extract: (id: string, documentId: string) =>
    request(() =>
      api.post<LegacyCaseDetail>(
        `/legacy-cases/${id}/extract`,
        { documentId },
        { timeout: 120000 }
      )
    ),
  import: (id: string, payload: LegacyImportRequest) =>
    request(() =>
      api.post<LegacyImportResult>(`/legacy-cases/${id}/import`, payload)
    ),
  document: (contentUrl: string) =>
    request(async () => {
      const url = contentUrl.replace(/^(?:\/api)?\/lis\//, "/");
      if (!/^\/(legacy-cases|cases)\//.test(url))
        throw new LegacyCaseRequestError(400);
      const response = await apiClient.get<Blob>(url, { responseType: "blob" });
      return response.data;
    }),
};
